import {
  COMMUNICATION_CHANNEL_OPTIONS,
  CommunicationChannel,
} from "../../constants/communicationConstants";
import {
  isChannelSelectable,
  toggleChannel,
} from "../../utils/helpers/communicationPreferencesHelpers";

interface ChannelSelectorProps {
  channels: CommunicationChannel[];
  onChange: (channels: CommunicationChannel[]) => void;
  email?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  mobilePhoneVerified?: boolean;
  hasFaxNumber?: boolean;
  readOnly?: boolean;
  onAddVerifyPhone?: () => void;
}

function ChannelSelector({
  channels,
  onChange,
  email,
  mobilePhone,
  fax,
  mobilePhoneVerified,
  hasFaxNumber,
  readOnly = false,
  onAddVerifyPhone,
}: ChannelSelectorProps): JSX.Element {
  const contactInfo = {
    email,
    mobilePhone,
    fax,
    mobilePhoneVerified,
    hasFaxNumber,
  };

  return (
    <div>
      {COMMUNICATION_CHANNEL_OPTIONS.map((option, index) => {
        const isSelected = channels.includes(option.key);
        const { enabled, statusLabel, showVerifyLink } = isChannelSelectable(
          option.key,
          contactInfo
        );
        const isDisabled = readOnly || !enabled;

        return (
          <label
            key={option.key}
            className={`flex items-center justify-between gap-3 py-2.5 ${
              index > 0 ? "border-t border-dropDownBorder" : ""
            } ${isDisabled && !showVerifyLink ? "cursor-not-allowed" : ""} ${
              !isDisabled ? "cursor-pointer" : ""
            }`}
          >
            <div
              className={`flex items-center gap-3 min-w-0 ${
                isDisabled ? "opacity-70" : ""
              }`}
            >
              <span className="relative inline-flex h-5 w-5 shrink-0">
                <input
                  type="checkbox"
                  className="peer h-5 w-5 appearance-none rounded border border-dropDownBorder bg-white cursor-pointer disabled:cursor-not-allowed checked:border-primary checked:bg-primary"
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => {
                    if (!isDisabled) {
                      onChange(toggleChannel(channels, option.key));
                    }
                  }}
                />
                <svg
                  className="pointer-events-none absolute inset-0 m-auto h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="font-semibold text-royalBlue shrink-0">
                {option.label}
              </span>
              <span className="text-sm text-textSecondaryGray truncate">
                {statusLabel}
              </span>
            </div>
            {showVerifyLink && (
              <button
                type="button"
                className="text-sm text-primary font-semibold whitespace-nowrap shrink-0 hover:text-primaryHover"
                onClick={(e) => {
                  e.preventDefault();
                  onAddVerifyPhone?.();
                }}
              >
                Add & verify →
              </button>
            )}
          </label>
        );
      })}
      <p className="pt-2 text-xs text-textSecondaryGray">
        Notifications are always available in the portal.
      </p>
    </div>
  );
}

export default ChannelSelector;
